import { Box, Typography } from "@material-ui/core";
import React, { FC } from "react";
import {
  injectIntl,
  WrappedComponentProps,
  FormattedMessage,
} from "react-intl";
import { useNavigate } from "react-router-dom";
import getIdGif from "../Assets/getId.gif";
import useStyles from "./styles";
import { useApolloClient, useMutation } from "@apollo/client";
import { CREATE_GET_ID } from "../../graphql/onboarding/member/mutation";
import { SapheerError } from "../../enums/error";
import {
  CreateKycApplication,
  CreateKycApplicationVariables,
} from "../../graphql/onboarding/member/types/CreateKycApplication";
import GetIdTitle from "./GetIdTitle";
import { CustomButton } from "../Ui/CustomButton";
import { Hourglass, UserSquare } from "lucide-react";
import { useToastNotifications } from "../../utils/toastUtils";

interface GetIdProps {
  next: string;
  previous: string;
  onProcessing: boolean;
}

const GetId: FC<GetIdProps & WrappedComponentProps> = ({
  next,
  intl,
  onProcessing,
  previous,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const { showErrorToast } = useToastNotifications();
  const [createGetId, { loading }] = useMutation<
    CreateKycApplication,
    CreateKycApplicationVariables
  >(CREATE_GET_ID, {
    onCompleted: ({ createKycApplication }) => {
      if (createKycApplication) {
        navigate("/identity-verification");
        window.location.replace(createKycApplication);
      }
    },
    onError: ({ graphQLErrors }) => {
      let message = intl.formatMessage({ id: "create.get.id.error" });
      if (
        graphQLErrors?.[0]?.message?.includes(
          SapheerError.STRIPE_PAYMENT_NOT_PAID,
        )
      ) {
        message = intl.formatMessage({ id: "payment.stripe.not.paid" });
      }
      showErrorToast({
        description: message,
      });
    },
  });

  const navigateTo = (path: string) => navigate(path);

  const handleVerify = () => {
    const successUrl = window.location.href.replace(
      "identity-verification",
      "contrat",
    );
    createGetId({
      variables: { failedUrl: successUrl, succesUrl: successUrl },
    });
  };

  return (
    <Box maxWidth="md">
      <Box className={classes.container}>
        <img src={getIdGif} className={classes.gif} width="100%" alt="Get ID" />
        {onProcessing ? (
          <>
            <Typography className={classes.text} variant="subtitle1">
              <FormattedMessage id="get.id.text1.processing" />
            </Typography>
            <Typography className={classes.text} variant="subtitle1">
              <FormattedMessage id="get.id.text2.processing" />
            </Typography>
          </>
        ) : (
          <>
            <GetIdTitle />
          </>
        )}

        <Box className={classes.button}>
          {onProcessing ? (
            <>
              <CustomButton
                type="button"
                variant="outline4"
                onClick={() => navigateTo(previous)}
                label="button.previous"
                disabled={loading}
                fullWidthMobile
              />
              <CustomButton
                type="button"
                variant="primary"
                onClick={() => navigateTo(next)}
                label="button.next"
                fullWidthMobile
              />
            </>
          ) : (
            <>
              <CustomButton
                type="button"
                variant="primary"
                onClick={handleVerify}
                label="get.id.button.verify"
                fullWidthMobile
                startIcon={<UserSquare className="w-4 h-4 mr-2" />}
              />
              <CustomButton
                type="button"
                variant="outline4"
                onClick={() => navigateTo(next)}
                label="get.id.button.never"
                disabled={loading}
                fullWidthMobile
                startIcon={<Hourglass className="w-4 h-4 mr-2" />}
              />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default injectIntl(GetId);
